import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  FormGroup,
} from '@material-ui/core';

import i18n from 'i18next';

import ReCAPTCHA from 'react-google-recaptcha';

import MenuItem from '@material-ui/core/MenuItem';
import useTempIEStyles from './useTempIEStyles';
import { regions } from '../../constants/regions';

const numberClamp = {
  onKeyPress: e => {
    const keyCode = (e.which) ? e.which : e.keyCode;
    if ((keyCode < 48 || keyCode > 57)) {
      e.preventDefault();
    }
  },
};

const languages = () => [
  {
    label: i18n.t('languages:option'),
    value: 0,
  },
  {
    label: i18n.t('languages:English'),
    value: 'E',
  },
  {
    label: i18n.t('languages:French'),
    value: 'F',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {},
  phone: {
    '& > * + * ': {
      marginLeft: 10,
    },
  },
  margin: {
    margin: theme.spacing(1, 0),
  },
  header: {
    marginBottom: 16,
  },
}));

const RegisterationForm = ({
  values: { name, company, phone, ext, email, region, language, services = [] },
  errors,
  touched,
  handleSubmit,
  handleChange,
  isValid,
  setFieldTouched,
  setFieldValue,
  validateForm,
  isComplete,
  onChange,
}) => {
  const classes = useStyles();
  const ieStyles = useTempIEStyles();
  const history = useHistory();

  const capchaChange = (value) => {
    setFieldValue('code', value, true);

    if (!isValid || value === null) {
      validateForm();
    }
  };

  const change = async (event) => {
    onChange(event);
    event.persist();
    const target = event.target;

    setFieldTouched(target.id, true, false);
    await handleChange(event);

    if (!isValid) {
      validateForm();
    }
  };

  const checkboxChange = (event) => {
    const target = event.currentTarget;

    if (target.checked) {
      services.push(target.id);
    } else {
      services.splice(services.indexOf(target.id), 1);
    }

    setFieldValue(target.id, services);
    setFieldTouched(target.id, true);
  };

  const handleClickCancel = () => {
    history.push('/login');
  };

  const handleReturnToLoginClick = () => {
    history.push('/login');
  };

  return (
    <div className={classes.root}>
      {!isComplete ? (
        <Fragment>
          <header className={classes.header}>
            <Typography variant="h2">{i18n.t('registration:title')}</Typography>
            <Typography variant="subtitle1">
              {i18n.t('registration:sub')}
            </Typography>
          </header>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <TextField
              id="name"
              name="name"
              variant="outlined"
              className={classes.margin}
              margin="dense"
              label={i18n.t('registration:form:name:label')}
              fullWidth
              value={name}
              required
              onChange={change}
              helperText={touched.name ? errors.name : ''}
              error={touched.name && Boolean(errors.name)}
            />
            <TextField
              id="company"
              name="company"
              variant="outlined"
              className={classes.margin}
              margin="dense"
              label={i18n.t('registration:form:company:label')}
              fullWidth
              required
              value={company}
              onChange={change}
              helperText={touched.company ? errors.company : ''}
              error={touched.company && Boolean(errors.company)}
            />
            <Grid container className={classes.phone}>
              <Grid item xs={9}>
                <TextField
                  id="phone"
                  name="phone"
                  variant="outlined"
                  inputProps={{ type: 'number', pattern: '[0-9]*' }}
                  className={classes.margin}
                  margin="dense"
                  label={i18n.t('registration:form:phone:label')}
                  fullWidth
                  required
                  value={phone}
                  {...numberClamp}
                  onChange={change}
                  helperText={touched.phone ? errors.phone : ''}
                  error={touched.phone && Boolean(errors.phone)}
                />
              </Grid>
              <Grid item xs>
                <TextField
                  id="ext"
                  name="ext"
                  variant="outlined"
                  inputProps={{ type: 'number', pattern: '[0-9]*' }}
                  className={classes.margin}
                  margin="dense"
                  label={i18n.t('registration:form:ext:label')}
                  fullWidth
                  {...numberClamp}
                  value={ext}
                  onChange={change}
                  helperText={touched.ext ? errors.ext : ''}
                  error={touched.ext && Boolean(errors.ext)}
                />
              </Grid>
            </Grid>
            <TextField
              id="email"
              name="email"
              variant="outlined"
              className={classes.margin}
              margin="dense"
              label={i18n.t('registration:form:email:label')}
              fullWidth
              required
              value={email}
              onChange={change}
              helperText={touched.email ? errors.email : ''}
              error={touched.email && Boolean(errors.email)}
            />
            <TextField
              id="region"
              name="region"
              size="small"
              variant="outlined"
              className={classes.margin}
              fullWidth
              required
              select
              label={i18n.t('registration:form:region:label')}
              value={region}
              onChange={change}
              helperText={touched.region ? errors.region : ''}
              error={touched.region && Boolean(errors.region)}
            >
              {regions.map((option, index) => (
                <MenuItem className={ieStyles.menuItem} key={option.value} value={option.value}>
                  {index === 0 ? i18n.t(`regions:${option.label}`) : option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="language"
              name="language"
              size="small"
              variant="outlined"
              className={classes.margin}
              fullWidth
              select
              required
              label={i18n.t('registration:form:language:label')}
              value={language}
              onChange={change}
              helperText={touched.language ? errors.language : ''}
              error={touched.language && Boolean(errors.language)}
            >
              {languages().map((option) => (
                <MenuItem className={ieStyles.menuItem} key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid item sm={4} xs={12}>
                <Typography variant="body1">
                  {i18n.t('registration:form:service:label')}
                </Typography>
              </Grid>
              <Grid item sm={8} xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="environmental"
                        name="environmental"
                        checked={services.includes('environmental')}
                        onChange={checkboxChange}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={i18n.t('registration:form:services:environmental')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="food"
                        name="food"
                        checked={services.includes('food')}
                        onChange={checkboxChange}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={i18n.t('registration:form:services:food')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="petroleum"
                        name="petroleum"
                        checked={services.includes('petroleum')}
                        onChange={checkboxChange}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={i18n.t('registration:form:services:petroleum')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="Hygiene"
                        name="Hygiene"
                        checked={services.includes('Hygiene')}
                        onChange={checkboxChange}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                    }
                    label={i18n.t('registration:form:services:Hygiene')}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <ReCAPTCHA
                  sitekey="6LdP5R4bAAAAAN-3OyR9PwzkAjanl_SwXGzqb1RD"
                  onChange={capchaChange}
                  size="normal"
                  hl={i18n.language.toLowerCase() === 'f' ? 'fr-CA' : 'en'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.margin}
                  type="submit"
                  disabled={!isValid}
                >
                  {i18n.t('registration:form:buttons:submit')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  color="primary"
                  className={classes.margin}
                  type="button"
                  onClick={handleClickCancel}
                >
                  {i18n.t('registration:form:buttons:cancel')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Fragment>
      ) : (
        <Fragment>
          <header className={classes.header}>
            <Typography variant="h2">
              {i18n.t('registration:complete:title')}
            </Typography>
            <Typography variant="body1">
              {i18n.t('registration:complete:sub')}
            </Typography>
          </header>
          <Typography variant="body1" color="error">
            {i18n.t('registration:complete:message')}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.margin}
            type="button"
            onClick={handleReturnToLoginClick}
          >
            {i18n.t('registration:complete:form:buttons:return')}
          </Button>
        </Fragment>
      )}
    </div>
  );
};

RegisterationForm.propTypes = {
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  isValid: PropTypes.bool,
  touched: PropTypes.shape({}),
  setFieldTouched: PropTypes.func,
  values: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  onChange: PropTypes.func,
  validateForm: PropTypes.func,
  setFieldValue: PropTypes.func,
  isComplete: PropTypes.bool,
};

export default RegisterationForm;



// WEBPACK FOOTER //
// ./src/components/Login/RegisterationForm.js